import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './styles/Status.css';
import Geohublogo from './Images/Geohublogoo.png';

const Status = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchStatusData();
    const interval = setInterval(fetchStatusData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatusData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://status.openai.com/api/v2/status.json');
      if (!response.ok) throw new Error('Failed to fetch OpenAI status data');
      const data = await response.json();
      setStatus(data.status);
      setLastUpdated(new Date(data.page.updated_at || Date.now()));
      setError(null);
    } catch (err) {
      setError('Unable to fetch OpenAI API status. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (indicator) => {
    switch (indicator) {
      case 'none':
        return '#10b981'; // green
      case 'minor':
        return '#f59e0b'; // yellow
      case 'major':
        return '#dc2626'; // red
      case 'critical':
        return '#8B0000'; // dark red
      default:
        return '#6b7280'; // gray
    }
  };

  const getStatusText = (indicator) => {
    switch (indicator) {
      case 'none':
        return 'All Systems Operational';
      case 'minor':
        return 'Degraded Performance';
      case 'major':
        return 'Major Outage';
      case 'critical':
        return 'Critical Outage';
      default:
        return 'Unknown Status';
    }
  };

  const getStatusIcon = (indicator) => {
    switch (indicator) {
      case 'none':
        return '✓';
      case 'minor':
        return '⚠';
      case 'major':
        return '✗';
      case 'critical':
        return '⛔';
      default:
        return '?';
    }
  };

  if (loading && !status) {
    return (
      <div className="status-container">
        <div className="status-header">
          <div className="status-logo">
            <img src={Geohublogo} alt="GeoHub Logo" />
            <h1>OpenAI API Status</h1>
          </div>
          <Link to="/" className="back-button">
            ← Back to Chat
          </Link>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading OpenAI API status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="status-container">
        <div className="status-header">
          <div className="status-logo">
            <img src={Geohublogo} alt="GeoHub Logo" />
            <h1>OpenAI API Status</h1>
          </div>
          <Link to="/" className="back-button">
            ← Back to Chat
          </Link>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠</div>
          <h2>Unable to Load OpenAI Status</h2>
          <p>{error}</p>
          <button onClick={fetchStatusData} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="status-container">
      <div className="status-header">
        <div className="status-logo">
          <img src={Geohublogo} alt="GeoHub Logo" />
          <h2>OpenAI API Status</h2>
        </div>
        <Link to="/" className="back-button">
          ← Back to Chat
        </Link>
      </div>

      <div className="status-content">
        <div className="overall-status">
          <div className="status-indicator" style={{ backgroundColor: getStatusColor(status?.indicator) }}>
            <span className="status-icon">{getStatusIcon(status?.indicator)}</span>
            <span className="status-text">{getStatusText(status?.indicator)}</span>
          </div>
          <p className="status-description">
            {status?.description || 'Monitoring OpenAI API services'}
          </p>
        </div>

        <div className="status-footer">
          <p>
            This status page monitors the OpenAI API services that power GeoHub's AI capabilities.
            {lastUpdated && (
              <span> Last updated: {new Date(lastUpdated).toLocaleString()}</span>
            )}
          </p>
          <div className="status-actions">
            <button onClick={fetchStatusData} className="refresh-button">
              Refresh Status
            </button>
            <a 
              href="https://status.openai.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="external-link"
            >
              View Official OpenAI Status Page
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Status; 